/** Payment and check vouchers, petty cash (M05-05, M05-11). */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Plus, Send, Stamp, Trash2, Undo2 } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Decimal, amount, dec, parseAmount } from '../../lib/money';
import { shortDate } from '../../lib/format';
import { manilaToday, useAccounts, useAtcs, useBranches, useContacts, useTaxCodes } from '../../lib/queries';
import { AccountSelect } from '../../components/AccountSelect';
import { ReasonDialog } from '../../components/ReasonDialog';
import { Alert, Button, Card, EmptyState, Field, FilterBar, Modal, Money, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, filterClass, inputClass } from '../../components/ui';
import { METHOD_LABEL } from '../sales/ReceiptsPages';
import { VAT_CLASS_LABEL, type InputVatClass, type PurchaseSummary } from './types';

interface PaymentView {
  id: string;
  number: string | null;
  status: 'DRAFT' | 'PENDING_APPROVAL' | 'POSTED' | 'VOIDED';
  branchId: string;
  contactId: string;
  supplier?: string;
  payeeName: string;
  date: string;
  method: string;
  cashAccountId: string;
  checkNumber: string;
  checkDate: string | null;
  releaseStatus: 'NOT_APPLICABLE' | 'UNRELEASED' | 'RELEASED' | 'CLEARED';
  amountPaid: string;
  ewtAmount: string;
  unallocated: string;
  reference: string;
  notes: string;
  journalEntryId: string | null;
  createdById: string;
  voidReason: string | null;
  rowVersion: number;
  allocations: { billId: string; amount: string; ewtAmount: string; number: string | null; supplierInvoiceNo: string; billBalance: string | null }[];
  lines: { id: string; description: string; accountId: string; taxCode: string; inputVatClass: InputVatClass; atc: string | null; amount: string; netAmount: string; vatAmount: string; ewtAmount: string }[];
}

const TONE = { DRAFT: 'neutral', PENDING_APPROVAL: 'warning', POSTED: 'ok', VOIDED: 'critical' } as const;
const LABEL = { DRAFT: 'Draft', PENDING_APPROVAL: 'Awaiting approval', POSTED: 'Posted', VOIDED: 'Voided' } as const;
const RELEASE = { NOT_APPLICABLE: '', UNRELEASED: 'Check not yet released', RELEASED: 'Released', CLEARED: 'Cleared' } as const;

export function PaymentsPage(): JSX.Element {
  const { can } = useAuth();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const params = new URLSearchParams({ pageSize: '100' });
  if (q.trim()) params.set('q', q.trim());
  if (status) params.set('status', status);
  const list = useQuery({ queryKey: ['payments', params.toString()], queryFn: () => api<{ data: PaymentView[] }>(`/payments?${params.toString()}`), placeholderData: keepPreviousData });
  return (
    <>
      <PageHeader title="Payments" description="Check and payment vouchers, and petty cash disbursements." actions={can('payments.create', 'pettycash.manage') && <Link to="/payments/new"><Button><Plus aria-hidden="true" className="h-4 w-4" /> New payment</Button></Link>} />
      <FilterBar>
        <input className={`${filterClass} w-56`} placeholder="Number, check no. or payee" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search" />
        <select className={filterClass} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="">All statuses</option>
          {Object.entries(LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </FilterBar>
      <Card>
        {list.isLoading && <Skeleton className="h-48 w-full" />}
        {list.data?.data.length === 0 && <EmptyState title="No payments yet" />}
        {list.data && list.data.data.length > 0 && (
          <TableWrap>
            <thead><tr className="border-b border-slate-200 dark:border-slate-800"><Th>Date</Th><Th>Number</Th><Th>Payee</Th><Th>Method</Th><Th align="right">Paid</Th><Th align="right">EWT</Th><Th>Status</Th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {list.data.data.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <Td className="whitespace-nowrap">{shortDate(p.date)}</Td>
                  <Td className="whitespace-nowrap font-medium"><Link className="text-brand-700 hover:underline dark:text-brand-100" to={`/payments/${p.id}`}>{p.number ?? 'Draft'}</Link></Td>
                  <Td className="max-w-xs truncate">{p.payeeName || p.supplier}</Td>
                  <Td>{METHOD_LABEL[p.method]}{p.checkNumber ? ` #${p.checkNumber}` : ''}</Td>
                  <Td align="right"><Money>{amount(p.amountPaid)}</Money></Td>
                  <Td align="right"><Money>{amount(p.ewtAmount, true)}</Money></Td>
                  <Td><StatusChip tone={TONE[p.status]}>{LABEL[p.status]}</StatusChip>{p.releaseStatus === 'UNRELEASED' && p.status === 'POSTED' && <span className="ml-2"><StatusChip tone="warning">Unreleased</StatusChip></span>}</Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>
    </>
  );
}

interface DirectLine {
  key: number;
  description: string;
  amount: string;
  taxCode: string;
  inputVatClass: InputVatClass;
  accountId: string;
  atc: string;
}
let nextKey = 1;

export function PaymentEditorPage(): JSX.Element {
  const { id } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const suppliers = useContacts('supplier');
  const branches = useBranches();
  const accounts = useAccounts();
  const taxCodes = useTaxCodes();
  const atcs = useAtcs();
  const existing = useQuery({ queryKey: ['payment', id], queryFn: () => api<PaymentView>(`/payments/${id}`), enabled: Boolean(id) });

  const [contactId, setContactId] = useState(search.get('contact') ?? '');
  const [payeeName, setPayeeName] = useState('');
  const [branchId, setBranchId] = useState('');
  const [date, setDate] = useState(manilaToday());
  const [method, setMethod] = useState('CHECK');
  const [cashAccountId, setCashAccountId] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [checkDate, setCheckDate] = useState('');
  const [reference, setReference] = useState('');
  const [amountPaid, setAmountPaid] = useState('');
  const [alloc, setAlloc] = useState<Record<string, string>>({});
  const [lines, setLines] = useState<DirectLine[]>([]);
  const [error, setError] = useState<ApiError | null>(null);

  const supplier = suppliers.data?.find((s) => s.id === contactId);
  const bills = useQuery({
    queryKey: ['purchases', 'open-bills', contactId],
    queryFn: () => api<{ data: PurchaseSummary[] }>(`/purchase-documents?kind=BILL&status=POSTED&openOnly=true&contactId=${contactId}&pageSize=200`).then((r) => r.data),
    enabled: Boolean(contactId),
  });

  useEffect(() => {
    if (!branchId && branches.data?.length) setBranchId(branches.data.find((b) => b.isHeadOffice)?.id ?? branches.data[0].id);
  }, [branches.data, branchId]);
  useEffect(() => {
    const p = existing.data;
    if (!p) return;
    setContactId(p.contactId); setPayeeName(p.payeeName); setBranchId(p.branchId); setDate(p.date); setMethod(p.method); setCashAccountId(p.cashAccountId);
    setCheckNumber(p.checkNumber); setCheckDate(p.checkDate ?? ''); setReference(p.reference); setAmountPaid(dec(p.amountPaid).toFixed(2));
    setAlloc(Object.fromEntries(p.allocations.map((a) => [a.billId, dec(a.amount).toFixed(2)])));
    setLines(p.lines.map((l) => ({ key: nextKey++, description: l.description, amount: dec(l.amount).toFixed(2), taxCode: l.taxCode, inputVatClass: l.inputVatClass, accountId: l.accountId, atc: l.atc ?? '' })));
  }, [existing.data]);
  useEffect(() => {
    const billId = search.get('bill');
    if (!id && billId && bills.data && !(billId in alloc)) {
      const b = bills.data.find((x) => x.id === billId);
      if (b) setAlloc({ [billId]: dec(b.balanceDue).toFixed(2) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bills.data]);

  /** What will go out, mirroring the server: bills less payment-basis EWT, lines less EWT. */
  const computed = useMemo(() => {
    let cash = new Decimal(0);
    let ewt = new Decimal(0);
    for (const b of bills.data ?? []) {
      const a = dec(parseAmount(alloc[b.id] ?? '') ?? 0);
      if (a.isZero()) continue;
      const w = b.ewtAtBooking || dec(b.payableAmount).isZero() ? new Decimal(0) : dec(b.ewtAmount).times(a).div(dec(b.payableAmount)).toDecimalPlaces(2);
      cash = cash.plus(a.minus(w));
      ewt = ewt.plus(w);
    }
    for (const l of lines) {
      const gross = dec(parseAmount(l.amount) ?? 0);
      const code = taxCodes.data?.find((t) => t.code === l.taxCode);
      const vat = code && code.kind === 'VAT' && code.treatment === 'VATABLE';
      const net = vat ? gross.div(dec(code!.rate).plus(1)).toDecimalPlaces(2) : gross;
      const atc = atcs.data?.find((a) => a.code === l.atc);
      const w = atc ? (supplier?.vatStatus === 'VAT' ? net : gross).times(dec(atc.rate)).toDecimalPlaces(2) : new Decimal(0);
      cash = cash.plus(gross.minus(w));
      ewt = ewt.plus(w);
    }
    return { cash, ewt };
  }, [alloc, bills.data, lines, taxCodes.data, atcs.data, supplier]);

  const save = useMutation({
    mutationFn: async (then: 'draft' | 'submit') => {
      const body = {
        branchId, contactId, payeeName, date, method, cashAccountId, checkNumber, checkDate: checkDate || null, reference,
        amountPaid: parseAmount(amountPaid) ?? undefined,
        rowVersion: existing.data?.rowVersion,
        allocations: Object.entries(alloc).flatMap(([billId, v]) => (parseAmount(v) && dec(parseAmount(v)!).gt(0) ? [{ billId, amount: parseAmount(v)! }] : [])),
        lines: lines.filter((l) => parseAmount(l.amount)).map((l) => ({ description: l.description, amount: parseAmount(l.amount)!, taxCode: l.taxCode, inputVatClass: l.inputVatClass, accountId: l.accountId, atc: l.atc || null })),
      };
      let p = id ? await api<PaymentView>(`/payments/${id}`, { method: 'PATCH', body }) : await api<PaymentView>('/payments', { method: 'POST', body });
      if (then === 'submit') p = await api<PaymentView>(`/payments/${p.id}/submit`, { method: 'POST' });
      return p;
    },
    onSuccess: (p) => {
      void queryClient.invalidateQueries({ queryKey: ['payments'] });
      void queryClient.invalidateQueries({ queryKey: ['purchases'] });
      navigate(`/payments/${p.id}`, { replace: true });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught : new ApiError(0, 'Could not save.')),
  });

  if (id && existing.isLoading) return <Skeleton className="h-96 w-full" />;
  const cashAccounts = (accounts.data ?? []).filter((a) => a.isCash && a.isPosting && a.active);

  return (
    <>
      <PageHeader title={id ? 'Edit payment' : 'New payment'} description="Pay open bills, or record an expense paid on the spot. Petty cash: choose the Petty Cash Fund as the source." />
      {error && <div className="mb-4"><Alert tone="critical">{error.message}{error.fieldMessages.length > 1 && <ul className="mt-1 list-disc pl-5">{error.fieldMessages.slice(1, 5).map((m) => <li key={m}>{m}</li>)}</ul>}</Alert></div>}
      <Card className="mb-4">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="md:col-span-2">
            <Field label="Supplier / payee" required>
              <select className={inputClass} value={contactId} onChange={(e) => { setContactId(e.target.value); setAlloc({}); }}>
                <option value="">Choose</option>
                {(suppliers.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.registeredName}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Pay to the order of" hint="Printed on the check. Defaults to the supplier."><input className={inputClass} value={payeeName} onChange={(e) => setPayeeName(e.target.value)} /></Field>
          <Field label="Date" required><input type="date" className={inputClass} value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Field label="Method"><select className={inputClass} value={method} onChange={(e) => setMethod(e.target.value)}>{Object.entries(METHOD_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
          <Field label="Paid from" required error={error?.fields.cashAccountId}>
            <select className={inputClass} value={cashAccountId} onChange={(e) => setCashAccountId(e.target.value)}>
              <option value="">Choose an account</option>
              {cashAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
            </select>
          </Field>
          {method === 'CHECK' && (
            <>
              <Field label="Check no." required error={error?.fields.checkNumber}><input className={inputClass} value={checkNumber} onChange={(e) => setCheckNumber(e.target.value)} /></Field>
              <Field label="Check date" hint="Post-dated? Enter the date on the check."><input type="date" className={inputClass} value={checkDate} onChange={(e) => setCheckDate(e.target.value)} /></Field>
            </>
          )}
          <Field label="Branch"><select className={inputClass} value={branchId} onChange={(e) => setBranchId(e.target.value)}>{(branches.data ?? []).filter((b) => b.active).map((b) => <option key={b.id} value={b.id}>{b.code} {b.name}</option>)}</select></Field>
          <Field label="Reference"><input className={inputClass} value={reference} onChange={(e) => setReference(e.target.value)} /></Field>
        </div>
      </Card>

      <Card title="Bills to pay" className="mb-4">
        {!contactId && <EmptyState title="Choose the supplier first" />}
        {bills.data?.length === 0 && <EmptyState title="No open bills" description="Add expense lines below for something paid on the spot." />}
        {bills.data && bills.data.length > 0 && (
          <TableWrap>
            <thead><tr className="border-b border-slate-200 dark:border-slate-800"><Th>Bill</Th><Th>Their invoice</Th><Th>Due</Th><Th align="right">Balance</Th><Th>EWT</Th><Th align="right">Pay</Th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {bills.data.map((b) => (
                <tr key={b.id}>
                  <Td className="whitespace-nowrap font-medium">{b.number}</Td>
                  <Td>{b.supplierInvoiceNo}</Td>
                  <Td className="whitespace-nowrap">{shortDate(b.dueDate)}</Td>
                  <Td align="right"><Money>{amount(b.balanceDue)}</Money></Td>
                  <Td className="text-xs text-slate-500">{b.ewtAtBooking ? 'already withheld' : 'withheld now'}</Td>
                  <Td align="right">
                    <div className="flex items-center justify-end gap-2">
                      <button type="button" className="text-xs text-brand-700 hover:underline dark:text-brand-100" onClick={() => setAlloc((a) => ({ ...a, [b.id]: dec(b.balanceDue).toFixed(2) }))}>Full</button>
                      <input className={`${filterClass} w-32 text-right tabular-nums`} inputMode="decimal" value={alloc[b.id] ?? ''} onChange={(e) => setAlloc((a) => ({ ...a, [b.id]: e.target.value }))} aria-label={`Pay ${b.number}`} />
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>

      <Card title="Expenses paid directly" actions={<Button size="sm" variant="secondary" onClick={() => setLines((all) => [...all, { key: nextKey++, description: '', amount: '', taxCode: supplier?.vatStatus === 'VAT' ? 'VAT12' : 'NONVAT', inputVatClass: 'GOODS', accountId: supplier?.defaultExpenseAccountId ?? '', atc: '' }])}><Plus aria-hidden="true" className="h-4 w-4" /> Add expense</Button>}>
        {lines.length === 0 ? <p className="text-sm text-slate-500">None. Use this for petty cash or on-the-spot purchases with an official receipt.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead><tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500"><th className="pb-2 pr-2">Description</th><th className="w-48 pb-2 pr-2">Account</th><th className="w-32 pb-2 pr-2">Tax</th><th className="w-32 pb-2 pr-2">VAT class</th><th className="w-28 pb-2 pr-2">ATC</th><th className="w-32 pb-2 pr-2 text-right">Amount (incl. VAT)</th><th className="w-10" /></tr></thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={l.key}>
                    <td className="py-1.5 pr-2"><input className={`${filterClass} w-full`} value={l.description} onChange={(e) => setLines((all) => all.map((x) => (x.key === l.key ? { ...x, description: e.target.value } : x)))} aria-label={`Expense ${i + 1} description`} /></td>
                    <td className="py-1.5 pr-2"><AccountSelect accounts={(accounts.data ?? []).filter((a) => a.type === 'EXPENSE' || a.type === 'ASSET')} value={l.accountId} onChange={(accountId) => setLines((all) => all.map((x) => (x.key === l.key ? { ...x, accountId } : x)))} className={`${filterClass} w-full`} /></td>
                    <td className="py-1.5 pr-2"><select className={`${filterClass} w-full`} value={l.taxCode} onChange={(e) => setLines((all) => all.map((x) => (x.key === l.key ? { ...x, taxCode: e.target.value } : x)))}>{(taxCodes.data ?? []).filter((t) => t.kind !== 'PERCENTAGE').map((t) => <option key={t.code} value={t.code}>{t.name}</option>)}</select></td>
                    <td className="py-1.5 pr-2"><select className={`${filterClass} w-full`} value={l.inputVatClass} onChange={(e) => setLines((all) => all.map((x) => (x.key === l.key ? { ...x, inputVatClass: e.target.value as InputVatClass } : x)))}>{Object.entries(VAT_CLASS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></td>
                    <td className="py-1.5 pr-2"><select className={`${filterClass} w-full`} value={l.atc} onChange={(e) => setLines((all) => all.map((x) => (x.key === l.key ? { ...x, atc: e.target.value } : x)))}><option value="">None</option>{(atcs.data ?? []).filter((a) => a.kind === 'EWT' && a.active).map((a) => <option key={a.code} value={a.code}>{a.code}</option>)}</select></td>
                    <td className="py-1.5 pr-2"><input className={`${filterClass} w-full text-right tabular-nums`} inputMode="decimal" value={l.amount} onChange={(e) => setLines((all) => all.map((x) => (x.key === l.key ? { ...x, amount: e.target.value } : x)))} aria-label={`Expense ${i + 1} amount`} /></td>
                    <td><button type="button" className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:text-red-600" aria-label={`Remove expense ${i + 1}`} onClick={() => setLines((all) => all.filter((x) => x.key !== l.key))}><Trash2 aria-hidden="true" className="h-4 w-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <Field label="Amount paid" hint={`Leave blank to pay exactly ${amount(computed.cash.toString())}. More than that becomes an advance.`}>
            <input className={`${inputClass} w-48 text-right tabular-nums`} inputMode="decimal" value={amountPaid} onChange={(e) => setAmountPaid(e.target.value)} placeholder={amount(computed.cash.toString())} />
          </Field>
          <dl className="min-w-[16rem] space-y-1 text-sm">
            <div className="flex justify-between gap-6"><dt className="text-slate-500">EWT withheld now</dt><dd><Money>{amount(computed.ewt.toString())}</Money></dd></div>
            <div className="flex justify-between gap-6 text-base font-bold"><dt>Cash out</dt><dd><Money>{amount((parseAmount(amountPaid) ?? computed.cash.toString()).toString())}</Money></dd></div>
          </dl>
        </div>
      </Card>

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button variant="secondary" onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="secondary" busy={save.isPending && save.variables === 'draft'} disabled={!contactId || !cashAccountId} onClick={() => { setError(null); save.mutate('draft'); }}>Save draft</Button>
        <Button busy={save.isPending && save.variables === 'submit'} disabled={!contactId || !cashAccountId} onClick={() => { setError(null); save.mutate('submit'); }}>Save and post</Button>
      </div>
    </>
  );
}

export function PaymentDetailPage(): JSX.Element {
  const { id } = useParams();
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<'void' | 'reject' | 'discard' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const p = useQuery({ queryKey: ['payment', id], queryFn: () => api<PaymentView>(`/payments/${id}`) });
  const refresh = (): void => { setDialog(null); setError(null); void queryClient.invalidateQueries({ queryKey: ['payment', id] }); void queryClient.invalidateQueries({ queryKey: ['payments'] }); void queryClient.invalidateQueries({ queryKey: ['purchases'] }); };
  const fail = (caught: unknown): void => setError(caught instanceof ApiError ? caught.message : 'That did not work.');
  const act = useMutation({ mutationFn: ({ path, body }: { path: string; body?: unknown }) => api(`/payments/${id}${path}`, { method: 'POST', body }), onSuccess: refresh, onError: fail });
  const discard = useMutation({ mutationFn: () => api(`/payments/${id}`, { method: 'DELETE' }), onSuccess: () => navigate('/payments'), onError: fail });
  if (p.isLoading) return <Skeleton className="h-96 w-full" />;
  if (!p.data) return <Alert tone="critical">Could not load this payment.</Alert>;
  const x = p.data;
  const isMaker = x.createdById === user?.id;
  return (
    <>
      <PageHeader
        title={`${x.method === 'CHECK' ? 'Check voucher' : 'Payment voucher'} ${x.number ?? '(draft)'}`}
        description={`${x.payeeName || x.supplier} · ${shortDate(x.date)} · ${METHOD_LABEL[x.method]}${x.checkNumber ? ` #${x.checkNumber}` : ''}`}
        actions={
          <>
            <StatusChip tone={TONE[x.status]}>{LABEL[x.status]}</StatusChip>
            {x.status === 'POSTED' && x.releaseStatus !== 'NOT_APPLICABLE' && <StatusChip tone={x.releaseStatus === 'UNRELEASED' ? 'warning' : 'ok'}>{RELEASE[x.releaseStatus]}</StatusChip>}
            {x.status === 'DRAFT' && can('payments.create', 'pettycash.manage') && (
              <>
                <Link to={`/payments/${x.id}/edit`}><Button variant="secondary">Edit</Button></Link>
                <Button variant="secondary" onClick={() => setDialog('discard')}><Trash2 aria-hidden="true" className="h-4 w-4" /> Discard</Button>
                <Button busy={act.isPending} onClick={() => act.mutate({ path: '/submit' })}><Send aria-hidden="true" className="h-4 w-4" /> Post</Button>
              </>
            )}
            {x.status === 'PENDING_APPROVAL' && can('payments.approve') && !isMaker && (
              <>
                <Button variant="secondary" onClick={() => setDialog('reject')}>Send back</Button>
                <Button busy={act.isPending} onClick={() => act.mutate({ path: '/approve' })}><CheckCircle2 aria-hidden="true" className="h-4 w-4" /> Approve</Button>
              </>
            )}
            {x.status === 'POSTED' && x.releaseStatus === 'UNRELEASED' && can('payments.post', 'payments.approve') && <Button busy={act.isPending} onClick={() => act.mutate({ path: '/release' })}><Stamp aria-hidden="true" className="h-4 w-4" /> Mark released</Button>}
            {x.status === 'POSTED' && can('void.approve') && <Button variant="danger" onClick={() => setDialog('void')}><Undo2 aria-hidden="true" className="h-4 w-4" /> Void</Button>}
          </>
        }
      />
      {error && !dialog && <div className="mb-4"><Alert tone="critical">{error}</Alert></div>}
      {x.status === 'PENDING_APPROVAL' && isMaker && <div className="mb-4"><Alert tone="warning">Above your approval limit — an approver must post it.</Alert></div>}
      {x.status === 'VOIDED' && <div className="mb-4"><Alert tone="critical">Voided: {x.voidReason}. The bills it paid are open again.</Alert></div>}
      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        <div className="space-y-4">
          {x.allocations.length > 0 && (
            <Card title="Bills paid">
              <TableWrap>
                <thead><tr className="border-b border-slate-200 dark:border-slate-800"><Th>Bill</Th><Th>Their invoice</Th><Th align="right">Settled</Th><Th align="right">EWT withheld</Th></tr></thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {x.allocations.map((a) => <tr key={a.billId}><Td><Link className="text-brand-700 hover:underline dark:text-brand-100" to={`/purchases/${a.billId}`}>{a.number}</Link></Td><Td>{a.supplierInvoiceNo}</Td><Td align="right"><Money>{amount(a.amount)}</Money></Td><Td align="right"><Money>{amount(a.ewtAmount, true)}</Money></Td></tr>)}
                </tbody>
              </TableWrap>
            </Card>
          )}
          {x.lines.length > 0 && (
            <Card title="Expenses paid directly">
              <TableWrap>
                <thead><tr className="border-b border-slate-200 dark:border-slate-800"><Th>Description</Th><Th align="right">Net</Th><Th align="right">VAT</Th><Th>ATC</Th><Th align="right">EWT</Th></tr></thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {x.lines.map((l) => <tr key={l.id}><Td>{l.description}</Td><Td align="right"><Money>{amount(l.netAmount)}</Money></Td><Td align="right"><Money>{amount(l.vatAmount, true)}</Money></Td><Td>{l.atc ?? '—'}</Td><Td align="right"><Money>{amount(l.ewtAmount, true)}</Money></Td></tr>)}
                </tbody>
              </TableWrap>
            </Card>
          )}
        </div>
        <Card title="Amounts">
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Paid</dt><dd><Money className="font-semibold">{amount(x.amountPaid)}</Money></dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">EWT withheld</dt><dd><Money>{amount(x.ewtAmount)}</Money></dd></div>
            {!dec(x.unallocated).isZero() && <div className="flex justify-between"><dt className="text-slate-500">Advance to supplier</dt><dd><Money>{amount(x.unallocated)}</Money></dd></div>}
            {x.checkDate && <div className="flex justify-between"><dt className="text-slate-500">Check date</dt><dd>{shortDate(x.checkDate)}</dd></div>}
            {x.journalEntryId && <div><dt className="text-slate-500">Ledger</dt><dd><Link className="text-brand-700 hover:underline dark:text-brand-100" to={`/journals/${x.journalEntryId}`}>View journal entry</Link></dd></div>}
          </dl>
        </Card>
      </div>
      {dialog === 'void' && <ReasonDialog title={`Void ${x.number}?`} description="The ledger entry is reversed, the bills reopen and the withholding is voided." confirmLabel="Void payment" withDate error={error} busy={act.isPending} onClose={() => { setDialog(null); setError(null); }} onConfirm={(reason, date) => act.mutate({ path: '/void', body: { reason, date } })} />}
      {dialog === 'reject' && <ReasonDialog title="Send back?" confirmLabel="Send back" tone="primary" error={error} busy={act.isPending} onClose={() => { setDialog(null); setError(null); }} onConfirm={(reason) => act.mutate({ path: '/reject', body: { reason } })} />}
      {dialog === 'discard' && <Modal title="Discard this draft?" onClose={() => setDialog(null)}><div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setDialog(null)}>Keep it</Button><Button variant="danger" busy={discard.isPending} onClick={() => discard.mutate()}>Discard</Button></div></Modal>}
    </>
  );
}
